// File: src/components/potus/MarketPerformanceChart.jsx
import React, { useEffect, useRef } from 'react';
import { Box, Paper, CircularProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { createChartOptions } from '../../utils/chartHelpers';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const MarketPerformanceChart = ({ selectedSector, marketData, loading = false }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const chartRef = useRef(null);

  const chartOptions = createChartOptions(selectedSector, marketData, isDarkMode);

  // Force chart update when theme changes
  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.update();
    }
  }, [isDarkMode, selectedSector]);

  return (
    <Paper
      variant="stats"
      sx={{
        p: 2,
        mb: 3,
        backgroundColor: theme.palette.mode === 'dark'
          ? 'rgba(19, 47, 76, 0.4)'
          : 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(10px)',
        border: theme.palette.mode === 'dark'
          ? '1px solid rgba(255, 255, 255, 0.1)'
          : '1px solid rgba(0, 0, 0, 0.1)',
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: 360,
          position: 'relative'
        }}
      >
        {loading ? (
          // Loading state with spinner
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              gap: 2
            }}
          >
            <CircularProgress 
              size={60}
              thickness={4}
              sx={{
                color: theme.palette.primary.main
              }}
            />
            <Typography 
              variant="h6" 
              color="text.secondary"
              sx={{ 
                fontWeight: 500,
                textAlign: 'center'
              }}
            >
              Loading {selectedSector} Performance...
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                textAlign: 'center',
                opacity: 0.7
              }}
            >
              Calculating market data and returns
            </Typography>
          </Box>
        ) : !chartOptions ? (
          // No data state
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              gap: 2
            }}
          >
            <Typography 
              variant="h6" 
              color="text.secondary"
              sx={{ 
                fontWeight: 500,
                textAlign: 'center'
              }}
            >
              No Data Available
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                textAlign: 'center',
                opacity: 0.7
              }}
            >
              Unable to load market performance data for {selectedSector}
            </Typography>
          </Box>
        ) : (
          // Chart display
          <Bar 
            ref={chartRef}
            data={chartOptions.data}
            options={chartOptions.options}
            plugins={chartOptions.plugins}
          />
        )}
      </Box>
    </Paper>
  );
};

export default MarketPerformanceChart;